import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { IMATCH_APP, isSessionJwt } from '../auth/session.utils';

function isRefreshOrLogout(url: string): boolean {
  return url.includes('/public/refresh') || url.includes('/public/logout');
}

function shouldAttemptRefresh(url: string, status: number, token: string | null): boolean {
  if (status !== 499 && status !== 401) {
    return false;
  }
  if (!isSessionJwt(token)) {
    return false;
  }
  if (isRefreshOrLogout(url)) {
    return false;
  }
  if (url.startsWith(environment.authUrl) && !url.includes('/public/me')) {
    return false;
  }
  return true;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const token = auth.token();
  const headers: Record<string, string> = { 'X-App': IMATCH_APP };
  if (token && !isRefreshOrLogout(req.url)) {
    headers['Token'] = token;
  }

  const authReq = req.clone({
    withCredentials: true,
    setHeaders: headers,
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!shouldAttemptRefresh(authReq.url, error.status, auth.token())) {
        return throwError(() => error);
      }
      return from(auth.refreshSession()).pipe(
        switchMap((ok) => {
          if (!ok) {
            return throwError(() => error);
          }
          const retried = authReq.clone({
            withCredentials: true,
            setHeaders: {
              'X-App': IMATCH_APP,
              Token: auth.token() ?? '',
            },
          });
          return next(retried);
        }),
      );
    }),
  );
};
